import React, { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import GetInfoService from "../../../../../../service/GetInfoService";
import UserEditService from "../../../../../../service/UserEditService";
import localeText from "../../../../../../locale";
import { useToastContext } from "../../../../../../contexts/ToastContext/ToastContext";
import { parsePhoneInput, formatPhoneForDisplay } from "../../utils/parsePhoneInput";

const getInfoService = new GetInfoService();
const userEditService = new UserEditService();

const ContactInfoForm = ({ user, setUserInfo }) => {
  const { showSuccessToast, showErrorToast } = useToastContext();
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");
  const [address, setAddress] = useState("");
  const [number, setNumber] = useState("");
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [loading, setLoading] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setPostalCode(user.postalCode ?? "");
    setCountry(user.country ?? "");
    setState(user.state ?? "");
    setCity(user.city ?? "");
    setDistrict(user.district ?? "");
    setAddress(user.address ?? "");
    setNumber(user.number ?? "");
    setPhoneDisplay(
      formatPhoneForDisplay(user.internationalCode, user.localCode, user.phoneNumber),
    );
  }, [user]);

  async function checkZipCode(zip) {
    if (!zip?.replace(/\D/g, "")) return;
    setZipLoading(true);
    try {
      const userCountry = await getInfoService.getUserCountry();
      const res = await getInfoService.getZipCodeInfo(zip.replace(/\D/g, ""), userCountry);
      if (res?.results && !Array.isArray(res.results)) {
        const key = Object.keys(res.results)[0];
        const first = res.results[key]?.[0];
        if (first) {
          setDistrict(first.city ?? "");
          setCity(first.province ?? "");
          setState(first.state_en ?? "");
        }
      }
    } catch (e) {}
    setZipLoading(false);
  }

  async function handleApply() {
    setLoading(true);
    const { internationalCode, localCode, phoneNumber } = parsePhoneInput(phoneDisplay);
    const username = localStorage.getItem("__extraconomy_username") || user?.username;
    const payload = {
      username: username || null,
      firstname: user?.firstname ?? null,
      surname: user?.surname ?? null,
      postalCode: postalCode || null,
      country: country || null,
      state: state || null,
      city: city || null,
      district: district || null,
      address: address || null,
      number: number || null,
      internationalCode: internationalCode || null,
      localCode: localCode || null,
      phoneNumber: phoneNumber || null,
      amountDaysToPickUp: user?.amountDaysToPickUp ?? user?.quantityOfDaysToPickUp ?? null,
      pickupOnSaturday: !!(user?.pickupOnSaturday ?? user?.canRetriveOnSaturdays),
    };
    Object.keys(payload).forEach((k) => (payload[k] = payload[k] === "" ? null : payload[k]));
    try {
      const res = await userEditService.postChangeProfileInfo(
        localStorage.getItem("__extraconomy_token"),
        payload,
      );
      if (res.messageCode !== 200000) {
        const msg = localeText("data")?.find((m) => m.code === res.messageCode)?.message;
        showErrorToast({ detail: msg || "error" });
      } else {
        showSuccessToast({ detail: "sucessfullyEditedProfile" });
        if (setUserInfo && res.result) setUserInfo(res.result);
      }
    } catch (e) {
      showErrorToast({ detail: "unexpectedSystemBehaviour" });
    } finally {
      setLoading(false);
    }
  }

  if (!user) return null;

  return (
    <div>
      <h2 className="text-lg font-semibold m-0 mb-1" style={{ color: "var(--theme-text)" }}>
        {localeText("contactInfo") || "Informações de contato"}
      </h2>
      <p className="text-sm m-0 mb-3" style={{ color: "var(--theme-text-muted)" }}>
        {localeText("contactInfoSubtitle") || "Informe seu telefone e endereço."}
      </p>
      <hr className="mb-3" />
      <div className="flex flex-column gap-3">
        <div>
          <label className="block text-sm mb-1 font-medium" style={{ color: "var(--theme-text)" }}>
            {localeText("phoneNumber")} *
          </label>
          <InputText
            value={phoneDisplay}
            onChange={(e) => setPhoneDisplay(e.target.value)}
            placeholder="+39 06 1234567"
            className="w-full p-no-outline border-round-md"
            style={{ color: "var(--theme-text)" }}
          />
        </div>
        <div>
          <label className="block text-sm mb-1 font-medium" style={{ color: "var(--theme-text)" }}>
            {localeText("zipCode")} *
          </label>
          <span className="p-input-icon-right w-full block">
            {zipLoading && <i className="pi pi-spin pi-spinner" />}
            <InputText
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              onBlur={(e) => checkZipCode(e.target.value)}
              disabled={zipLoading}
              style={{ color: "var(--theme-text)" }}
              className="w-full p-no-outline border-round-md"
            />
          </span>
        </div>
        <div>
          <label className="block text-sm mb-1 font-medium" style={{ color: "var(--theme-text)" }}>
            {localeText("country")} *
          </label>
          <InputText
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="w-full p-no-outline border-round-md"
            style={{ color: "var(--theme-text)" }}
          />
        </div>
        <div>
          <label className="block text-sm mb-1 font-medium" style={{ color: "var(--theme-text)" }}>
            {localeText("state")} *
          </label>
          <InputText
            value={state}
            onChange={(e) => setState(e.target.value)}
            className="w-full p-no-outline border-round-md"
            style={{ color: "var(--theme-text)" }}
          />
        </div>
        <div>
          <label className="block text-sm mb-1 font-medium" style={{ color: "var(--theme-text)" }}>
            {localeText("city")} *
          </label>
          <InputText
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className="w-full p-no-outline border-round-md"
            style={{ color: "var(--theme-text)" }}
          />
        </div>
        <div>
          <label className="block text-sm mb-1 font-medium" style={{ color: "var(--theme-text)" }}>
            {localeText("district")} *
          </label>
          <InputText
            value={district}
            onChange={(e) => setDistrict(e.target.value)}
            className="w-full p-no-outline border-round-md"
            style={{ color: "var(--theme-text)" }}
          />
        </div>
        <div>
          <label className="block text-sm mb-1 font-medium" style={{ color: "var(--theme-text)" }}>
            {localeText("publicPlace")} *
          </label>
          <InputText
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="w-full p-no-outline border-round-md"
            style={{ color: "var(--theme-text)" }}
          />
        </div>
        <div>
          <label className="block text-sm mb-1 font-medium" style={{ color: "var(--theme-text)" }}>
            {localeText("number")} *
          </label>
          <InputText
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            className="w-full p-no-outline border-round-md"
            style={{ color: "var(--theme-text)" }}
          />
        </div>
        <div className="flex gap-2 mt-2" style={{ height: "40px" }}>
          <Button
            label={localeText("cancel")}
            className="p-button-outlined border-round-md flex-1 text-sm font-medium"
            style={{ borderColor: "var(--theme-border)", color: "var(--theme-text-muted)" }}
            onClick={() => {
              setPostalCode("");
              setCountry("");
              setState("");
              setCity("");
              setDistrict("");
              setAddress("");
              setNumber("");
              setPhoneDisplay("");
            }}
          />
          <Button
            label={localeText("applyChanges") || localeText("confirmProfileChanges")}
            className="border-round-md flex-1 text-sm font-medium"
            loading={loading}
            style={{ backgroundColor: "var(--theme-primary)", color: "#fff", border: "none" }}
            onClick={handleApply}
          />
        </div>
      </div>
    </div>
  );
};

export default ContactInfoForm;
